import React, { useEffect, useState } from 'react';
import { Box, Chip, Typography, FormHelperText, CircularProgress } from '@mui/material';
import api from '../../../services/api';

/**
 * Multi-select chips for the persona representations whitelist. Used inside
 * the service-group editor and the therapist client-defaults editor. Empty
 * value array = "no narrowing" — all reps from the layer above are allowed.
 *
 * Reads the platform representations list (admin endpoint, available to anyone
 * with admin/clinical_supervisor/therapist roles via the auth chain — the
 * service-group editor and therapist editor mount only for those roles).
 */
const RepresentationWhitelist = ({ value = [], onChange, helperText = '' }) => {
  const [reps, setReps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get('/admin/persona-representations')
      .then((res) => {
        if (cancelled) return;
        setReps(Array.isArray(res.data) ? res.data : []);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e.response?.data?.error || 'Failed to load representations');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const toggle = (key) => {
    if (value.includes(key)) onChange(value.filter(k => k !== key));
    else onChange([...value, key]);
  };

  if (loading) {
    return <Box sx={{ my: 2 }}><CircularProgress size={20} /></Box>;
  }
  if (error) {
    return <Typography variant="caption" color="error" sx={{ display: 'block', my: 2 }}>{error}</Typography>;
  }
  if (reps.length === 0) {
    return <Typography variant="caption" color="text.secondary" sx={{ display: 'block', my: 2 }}>No representations defined yet.</Typography>;
  }

  const activeReps = reps.filter(r => r.isActive !== false);

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>Allowed persona representations</Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {activeReps.map(r => (
          <Chip
            key={r.key}
            label={r.name}
            color={value.includes(r.key) ? 'primary' : 'default'}
            variant={value.includes(r.key) ? 'filled' : 'outlined'}
            onClick={() => toggle(r.key)}
            avatar={r.avatarImageUrl ? <img src={r.avatarImageUrl} alt="" style={{ width: 24, height: 24, borderRadius: '50%' }} /> : undefined}
          />
        ))}
      </Box>
      <FormHelperText sx={{ ml: 0, mt: 1 }}>{helperText}</FormHelperText>
    </Box>
  );
};

export default RepresentationWhitelist;
